import { datadogLogs as WebDatadog } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import type { ContainerComponent } from '../../common/types/Common';
import { Singleton } from '../../common/utils/Singleton';
import {
    API_URL,
    FRUITFUL_ENV,
    LLM_SERVICE_BASE_URL,
} from '../../config/config';
import { IS_DEPLOYED } from '../../config/FruitfulEnvironment';
import type { ErrorData } from '../Logger';
import type { IDatadog } from './types/Datadog';

const DATADOG_CLIENT_TOKEN = 'pub43d4404ad33bc5da39de477d9b080b27';
const DATADOG_RUM_APPLICATION_ID = '16a7546b-f4fb-45a6-a745-c874772a8b54';
const SERVICE_NAME = 'grove-app';

const getLoggingConfig = () => ({
    clientToken: DATADOG_CLIENT_TOKEN,
    service: SERVICE_NAME,
    env: FRUITFUL_ENV,
});

const getRumConfig = () => ({
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    service: SERVICE_NAME,
    env: FRUITFUL_ENV,
    allowedTracingUrls: [
        {
            match: API_URL,
            propagatorTypes: ['datadog' as const],
        },
        {
            match: LLM_SERVICE_BASE_URL,
            propagatorTypes: ['datadog' as const],
        },
    ],
    traceContextInjection: 'sampled' as const,
});

class DatadogFacade extends Singleton implements IDatadog {
    public readonly Datadog: typeof WebDatadog.logger;

    constructor() {
        super();
        this.Datadog = WebDatadog.logger;
    }

    public init(): void {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.init(getRumConfig()); // DD RUM: https://docs.datadoghq.com/integrations/rum_react/
        WebDatadog.init(getLoggingConfig()); // DD Logs: https://docs.datadoghq.com/logs/log_collection/javascript/
    }

    public logError(exception: Error, data?: ErrorData) {
        if (!this.isEnabled()) {
            return;
        }

        datadogRum.addError(exception, data);
        this.Datadog.error(exception.message, data, exception);
    }

    public isEnabled(): boolean {
        return IS_DEPLOYED;
    }
}

export default DatadogFacade.getInstance();

export const DatadogProvider: ContainerComponent = ({ children }) => {
    return <>{children}</>;
};
